import { ClassicEditor, EventInfo } from 'ckeditor5'
import { ClassicEditorHandler } from 'components/WorkspaceEditor/components/ContentEditor'
import { detectContentChanges } from 'components/WorkspaceEditor/components/NodeContents/PlainText/plainTextChangeDetection'
import { PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { MutableRefObject, useCallback } from 'react'
import {
    useNodeMapActions,
    useNodeMapShallowSelector,
} from '../store/NodeMapStore'
import { getEditorReference } from '../utils'
import { EDITOR_OPERATION_DELAY_MSEC } from './useKeyOperation'

export interface UseExecuteSaveOperationParams {
    /** Reference to the CKEditor instance */
    editorRef: MutableRefObject<ClassicEditor | undefined>
    /** Blur handler to call when saving content */
    handleOnBlur: ClassicEditorHandler
    /** Set from handle key down state */
    setFromHandleKeyDown: (value: boolean) => void
    /** Content item ID being saved */
    contentItemId?: string
}

const { Enter } = PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING

/**
 * TODO: This hook seems to have similar implementation and behavior to handleOnBlur in usePlainTextHandlers.ts
 * Consider creating a common functionality to re-use, so we only update the save functionality in a single location.
 * Both implementations:
 * 1. Use detectContentChanges for change detection logic
 * 3. Call handleOnBlur with editor reference after a timeout
 * 4. Manage similar state updates and optimistic operations
 *
 * Potential refactoring: Extract common blur/save logic into a shared utility or hook
 * that both components can use with different configuration parameters.
 */

/** Custom hook to execute save operation in CKEditor using key operations.
 * Handles saving content and managing optimistic updates.
 * @param {UseExecuteSaveOperationParams} params - Parameters for the hook.
 * @return {Function} Function to execute save operation.
 */
export const useExecuteSaveOperation = ({
    editorRef,
    setFromHandleKeyDown,
    handleOnBlur,
    contentItemId,
}: UseExecuteSaveOperationParams) => {
    const { isSaveOperationInProgress } = useNodeMapShallowSelector(
        (state) => ({
            isSaveOperationInProgress: state.isSaveOperationInProgress,
        })
    )

    const {
        setSaveOperationInProgress,
        setSaveOperationKey,
    } = useNodeMapActions()

    const executeSaveOperation = useCallback(
        (
            event: React.KeyboardEvent<HTMLElement>,
            shortcutChecker: (
                event: React.KeyboardEvent<HTMLElement>
            ) => boolean,
            operationName: string,
            changeDetection?: ReturnType<typeof detectContentChanges>
        ): boolean => {
            if (!shortcutChecker(event)) return false

            const editor = getEditorReference(editorRef, operationName)
            if (!editor) return false

            if (isSaveOperationInProgress) {
                return true // Return true to indicate key was handled but operation blocked
            }

            if (operationName === Enter) {
                setFromHandleKeyDown(true)
            }

            setSaveOperationInProgress(true, contentItemId)
            setSaveOperationKey(operationName)

            setTimeout(() => {
                try {
                    handleOnBlur({} as EventInfo, editor)
                } finally {
                    setSaveOperationInProgress(false)
                }
            }, EDITOR_OPERATION_DELAY_MSEC)
            return true
        },
        [
            handleOnBlur,
            setFromHandleKeyDown,
            setSaveOperationInProgress,
            setSaveOperationKey,
            isSaveOperationInProgress,
            contentItemId,
        ]
    )

    return executeSaveOperation
}
